import { useEffect, useMemo, useState } from 'react';

import {
  BANK_ASSIGNABLE_ROLES,
  DEFAULT_PERMISSION_MATRIX,
  DISPLAY_TO_STORED,
  RBAC_MODULES,
  type RbacModule,
} from '@/features/auth/roles';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SettingsCheckbox } from '@/components/ui/settings-checkbox';
import { SettingsStatusPill } from '@/components/ui/settings-status-pill';

import { useDisplayRole } from '@/features/auth/useDisplayRole';

import { AddUserModal } from './AddUserModal';
import { SettingsCard, SettingsSection } from './settings-ui';
import type { PermissionMatrix, RoleAliases, SettingsUser } from './settings-api';
import {
  usePermissions,
  useRoleAliases,
  useSettingsUsers,
  useUpdatePermissions,
  useUpdateRoleAliases,
  useUserMutations,
} from './useSettings';

/** Max alias length, mirrors the backend cap (SCRUM-465). */
const MAX_ALIAS_LENGTH = 60;

/**
 * Settings › Roles & permissions (SCRUM-461): the permissions matrix (4 bank
 * roles × 5 modules, persisting toggles via /api/settings/permissions) and the
 * users table (real roster with status pills, role select, deactivate) + the
 * Add-user modal. Ports `renderSettingsSectionRbac`.
 *
 * The prototype's "Add role" custom-role affordance is NOT built — RBAC here is
 * config over the FIXED SystemRole set. Instead of custom roles, SCRUM-465 adds
 * per-tenant role NAME ALIASING (the Role names card below): a display-name
 * override per fixed role, never a new role. The matrix columns remain the four
 * system bank roles.
 */

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0])
    .join('')
    .toUpperCase();
}

export function RolesPermissionsSection() {
  const { users } = useSettingsUsers();

  return (
    <SettingsSection>
      <PermissionsMatrixCard users={users} />
      <RoleAliasesCard />
      <UsersCard users={users} />
    </SettingsSection>
  );
}

/* ── Role name aliases (SCRUM-465) ───────────────────────────────────────────── */

function RoleAliasesCard() {
  const { aliases: serverAliases, isLoading } = useRoleAliases();
  const { save } = useUpdateRoleAliases();

  // Local working copy keyed by STORED role value (the persisted key shape).
  const [working, setWorking] = useState<RoleAliases | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedTick, setSavedTick] = useState(false);

  useEffect(() => {
    if (serverAliases) setWorking(serverAliases);
  }, [serverAliases]);

  const effective: RoleAliases = working ?? {};

  const setAlias = (storedRole: string, value: string) => {
    setWorking((prev) => ({ ...(prev ?? {}), [storedRole]: value }));
    setSavedTick(false);
  };

  const onSave = () => {
    setSaving(true);
    // Drop blank/whitespace aliases so an empty field clears the override
    // (falls back to the built-in name) rather than persisting "".
    const cleaned: RoleAliases = {};
    for (const [stored, alias] of Object.entries(effective)) {
      if (alias.trim().length > 0) cleaned[stored] = alias.trim();
    }
    save(cleaned)
      .then(() => {
        setSavedTick(true);
      })
      .catch(() => {
        setSavedTick(false);
      })
      .finally(() => {
        setSaving(false);
      });
  };

  return (
    <SettingsCard
      title="Role names"
      headerRight={
        <Button onClick={onSave} disabled={saving || isLoading}>
          {saving ? 'Saving…' : savedTick ? 'Saved' : 'Save names'}
        </Button>
      }
    >
      <p className="text-text-2 mb-3 text-[0.8125rem]">
        Rename the built-in roles to match your institution&rsquo;s titles. This changes the display
        name only — role permissions and access are unchanged. Leave blank to use the default name.
      </p>

      {isLoading && !working ? (
        <div className="text-text-3 py-6 text-center text-sm">Loading role names…</div>
      ) : (
        <div className="flex flex-col gap-3">
          {BANK_ASSIGNABLE_ROLES.map((role) => {
            const stored = DISPLAY_TO_STORED[role];
            const value = stored ? (effective[stored] ?? '') : '';
            return (
              <div key={role} className="flex items-center gap-3">
                <label
                  htmlFor={`alias-${role}`}
                  className="text-text-1 w-36 flex-shrink-0 text-[0.8125rem] font-medium"
                >
                  {role}
                </label>
                <Input
                  id={`alias-${role}`}
                  value={value}
                  maxLength={MAX_ALIAS_LENGTH}
                  placeholder={role}
                  aria-label={`Display name for ${role}`}
                  onChange={(e) => {
                    if (stored) setAlias(stored, e.target.value);
                  }}
                  className="max-w-[min(320px,calc(100vw-4rem))]"
                />
              </div>
            );
          })}
        </div>
      )}
    </SettingsCard>
  );
}

/* ── Permissions matrix ──────────────────────────────────────────────────────── */

function PermissionsMatrixCard({ users }: { users: SettingsUser[] }) {
  const { matrix: serverMatrix, isError } = usePermissions();
  const { save } = useUpdatePermissions();
  const displayName = useDisplayRole();

  // Per-role assigned-user count for the column header (prototype
  // `.settings-matrix__count`). Matches the prototype's per-role `users.length`:
  // count users assigned to the role, excluding pending (no confirmed role yet)
  // and deactivated.
  const countForRole = (role: string) =>
    users.filter((u) => u.role === role && u.status === 'active').length;

  // Local working copy so a toggle is instant; persisted on change. Seeded from
  // the server matrix (which itself defaults to DEFAULT_PERMISSION_MATRIX).
  const [matrix, setMatrix] = useState<PermissionMatrix | null>(null);
  useEffect(() => {
    if (serverMatrix) setMatrix(serverMatrix);
  }, [serverMatrix]);

  const effective: PermissionMatrix = matrix ?? DEFAULT_PERMISSION_MATRIX;

  const toggle = (role: string, module: RbacModule) => {
    const next: PermissionMatrix = {
      ...effective,
      [role]: { ...effective[role], [module]: !effective[role]?.[module] },
    };
    setMatrix(next);
    void save(next);
  };

  return (
    <SettingsCard title="Permissions matrix">
      {isError && !matrix ? (
        <p className="text-text-3 py-2 text-[0.8125rem]">Permissions are unavailable right now.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="font-body w-full border-collapse">
            <thead>
              <tr>
                <th className="bg-bg-3 border-border-0 text-text-1 border-b px-3.5 py-3 text-left text-[0.75rem] font-semibold">
                  Module
                </th>
                {BANK_ASSIGNABLE_ROLES.map((role) => {
                  const n = countForRole(role);
                  return (
                    <th
                      key={role}
                      className="bg-bg-3 border-border-0 text-text-1 min-w-[130px] border-b px-3.5 py-3 text-center text-[0.75rem] font-semibold"
                    >
                      {displayName(role)}
                      <span className="text-text-3 mt-1 block text-center font-mono text-[0.5625rem] font-medium tracking-[0.06em] uppercase">
                        {`${String(n)} ${n === 1 ? 'user' : 'users'}`}
                      </span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {RBAC_MODULES.map((module) => (
                <tr key={module}>
                  <td className="border-border-0 text-text-0 border-b px-3.5 py-2.5 text-[0.8125rem] font-medium last:border-b-0">
                    {module}
                  </td>
                  {BANK_ASSIGNABLE_ROLES.map((role) => (
                    <td
                      key={role}
                      className="border-border-0 border-b px-3.5 py-2.5 text-center last:border-b-0"
                    >
                      <SettingsCheckbox
                        aria-label={`${role} — ${module}`}
                        checked={Boolean(effective[role]?.[module])}
                        onChange={() => {
                          toggle(role, module);
                        }}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </SettingsCard>
  );
}

/* ── Users table ─────────────────────────────────────────────────────────────── */

function UsersCard({ users }: { users: SettingsUser[] }) {
  const { assignRole, deactivate } = useUserMutations();
  const [modalOpen, setModalOpen] = useState(false);

  const pendingCount = useMemo(() => users.filter((u) => u.status === 'pending').length, [users]);

  return (
    <>
      <SettingsCard
        title="Users"
        headerRight={
          <button
            type="button"
            onClick={() => {
              setModalOpen(true);
            }}
            className="bg-bg-3 border-border-1 rounded-tk-sm text-text-1 hover:bg-bg-2 hover:text-text-0 font-body inline-flex items-center gap-1 border px-3 py-1.5 text-[0.75rem]"
          >
            <svg
              viewBox="0 0 12 12"
              width="10"
              height="10"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <line x1="6" y1="2.5" x2="6" y2="9.5" />
              <line x1="2.5" y1="6" x2="9.5" y2="6" />
            </svg>
            Add user
          </button>
        }
      >
        <div className="text-text-3 -mt-1 mb-1 flex items-center gap-2 text-[0.6875rem]">
          <span className="font-mono">{users.length} total</span>
          {pendingCount > 0 && (
            <span className="text-warning rounded-tk-full bg-[color-mix(in_srgb,var(--warning)_15%,transparent)] px-2 py-0.5 font-mono font-bold">
              {pendingCount} pending
            </span>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="font-body w-full border-collapse">
            <thead>
              <tr>
                {['Name', 'Branch', 'Role', 'Status', ''].map((h, i) => (
                  <th
                    key={h || `col-${String(i)}`}
                    className="bg-bg-3 border-border-0 text-text-3 border-b px-3.5 py-2.5 text-left font-mono text-[0.625rem] font-semibold tracking-[0.08em] uppercase"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr
                  key={u.id}
                  className={
                    u.status === 'pending'
                      ? 'bg-[color-mix(in_srgb,var(--warning)_7%,transparent)]'
                      : undefined
                  }
                >
                  <td className="border-border-0 border-b px-3.5 py-2.5 last:border-b-0">
                    <div className="flex items-center gap-2.5">
                      <span className="bg-brand-dim text-brand-primary rounded-tk-full inline-flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center font-mono text-[0.6875rem] font-semibold">
                        {initials(u.displayName)}
                      </span>
                      <div className="flex flex-col gap-px">
                        <div className="text-text-0 font-medium">{u.displayName}</div>
                        <div className="text-text-3 text-[0.6875rem]">{u.title ?? u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="border-border-0 text-text-1 border-b px-3.5 py-2.5 text-[0.8125rem] last:border-b-0">
                    {u.branch ?? '—'}
                  </td>
                  <td className="border-border-0 border-b px-3.5 py-2.5 last:border-b-0">
                    <RoleSelect
                      value={u.status === 'pending' ? '' : u.role}
                      pending={u.status === 'pending'}
                      onChange={(role) => {
                        void assignRole(u.id, role);
                      }}
                    />
                  </td>
                  <td className="border-border-0 border-b px-3.5 py-2.5 last:border-b-0">
                    <SettingsStatusPill status={u.status} />
                  </td>
                  <td className="border-border-0 border-b px-3.5 py-2.5 last:border-b-0">
                    {u.status !== 'deactivated' && (
                      <button
                        type="button"
                        aria-label={`Deactivate ${u.displayName}`}
                        onClick={() => {
                          void deactivate(u.id);
                        }}
                        className="text-text-3 rounded-tk-sm inline-flex h-6 w-6 items-center justify-center border-0 bg-transparent hover:bg-[rgba(200,74,74,0.10)] hover:text-[#c84a4a]"
                      >
                        <svg
                          viewBox="0 0 12 12"
                          width="11"
                          height="11"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          aria-hidden="true"
                        >
                          <line x1="3" y1="3" x2="9" y2="9" />
                          <line x1="9" y1="3" x2="3" y2="9" />
                        </svg>
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SettingsCard>

      {modalOpen && (
        <AddUserModal
          onClose={() => {
            setModalOpen(false);
          }}
        />
      )}
    </>
  );
}

function RoleSelect({
  value,
  pending,
  onChange,
}: {
  value: string;
  pending: boolean;
  onChange: (role: string) => void;
}) {
  return (
    <select
      aria-label="Assign role"
      value={value}
      onChange={(e) => {
        if (e.target.value) onChange(e.target.value);
      }}
      className="bg-bg-3 border-border-1 rounded-tk-sm text-text-0 hover:border-border-2 focus:border-brand-primary font-body cursor-pointer border py-1.5 pr-6 pl-2.5 text-[0.75rem] outline-none"
    >
      {pending && <option value="">Assign role…</option>}
      {BANK_ASSIGNABLE_ROLES.map((role) => (
        <option key={role} value={role}>
          {role}
        </option>
      ))}
    </select>
  );
}
